import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Download, FileText, Mail, Eye } from 'lucide-react';
import { usePayrollCalculations } from '@/hooks/usePayrollCalculations';
import { usePayrollPeriods } from '@/hooks/usePayrollPeriods';
import { generateSwissPayslip, downloadPayslip, type SwissPayslipData } from '@/utils/swissPayslipGenerator';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { COMPANY_INFO } from '@/config/companyInfo';

interface PayrollPDFManagerProps {
  payrollPeriodId: string;
}

export function PayrollPDFManager({ payrollPeriodId }: PayrollPDFManagerProps) {
  const { calculations, isLoading } = usePayrollCalculations(payrollPeriodId);
  const { periods } = usePayrollPeriods();
  const { toast } = useToast();
  
  const [generatingPDF, setGeneratingPDF] = useState<string>('');
  const [previewData, setPreviewData] = useState<SwissPayslipData | null>(null);

  const period = periods?.find(p => p.id === payrollPeriodId);

  const handleGeneratePDF = async (calculation: any) => {
    setGeneratingPDF(calculation.id);
    
    try {
      // Check for payroll override to determine if minimal rendering is needed
      const { getEmployeePayrollOverride } = await import('@/utils/payrollOverrides');
      const override = await getEmployeePayrollOverride(
        calculation.employee_id,
        new Date(period?.start_date || '')
      );

      const payslipData: SwissPayslipData = {
        employee: {
          id: calculation.employee_id,
          name: calculation.employees?.name || 'Unbekannt',
          address: 'Adresse wird geladen...', // TODO: Get from employee record
          ahv_number: 'XXX.XXXX.XXXX.XX', // TODO: Get from employee record
          employee_number: calculation.employee_id.slice(-6),
        },
        company: COMPANY_INFO,
        payroll: calculation,
        period: {
          start_date: period?.start_date || '',
          end_date: period?.end_date || '',
          payment_date: new Date().toISOString().split('T')[0],
        },
        minimal: override?.payslip_minimal || false,
      };

      const pdfBlob = await generateSwissPayslip(payslipData);
      const periodText = format(new Date(period?.start_date || ''), 'yyyy-MM');
      
      downloadPayslip(pdfBlob, payslipData.employee.name, periodText);
      
      toast({
        title: 'PDF Generated',
        description: `Payslip for ${payslipData.employee.name} has been downloaded.`,
      });
    } catch (error) {
      toast({
        title: 'PDF Generation Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setGeneratingPDF('');
    }
  };

  const handlePreview = (calculation: any) => {
    const payslipData: SwissPayslipData = {
      employee: {
        id: calculation.employee_id,
        name: calculation.employees?.name || 'Unbekannt',
        address: 'Musterstrasse 456\n8001 Zürich\nSchweiz',
        ahv_number: '756.1234.5678.90',
        employee_number: calculation.employee_id.slice(-6),
      },
      company: COMPANY_INFO,
      payroll: calculation,
      period: {
        start_date: period?.start_date || '',
        end_date: period?.end_date || '',
        payment_date: new Date().toISOString().split('T')[0],
      },
    };
    
    setPreviewData(payslipData);
  };

  const handleBulkGeneration = async () => {
    if (!calculations || calculations.length === 0) return;
    
    toast({
      title: 'Bulk Generation Started',
      description: `Generating ${calculations.length} payslips...`,
    });

    let successCount = 0;
    let errorCount = 0;

    for (const calculation of calculations) {
      try {
        await handleGeneratePDF(calculation);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }

    toast({
      title: 'Bulk Generation Complete',
      description: `${successCount} payslips generated successfully. ${errorCount} errors.`,
      variant: errorCount > 0 ? 'destructive' : 'default',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading payroll data...</div>
        </CardContent>
      </Card>
    );
  }

  if (!period) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Payroll period not found</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Swiss Payslip Generator
          </CardTitle>
          <CardDescription>
            Generate compliant Swiss payslips for {format(new Date(period.start_date), 'MMMM yyyy')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge>{calculations?.length || 0} Employees</Badge>
              <Badge variant="outline">{period.status}</Badge>
            </div>
            {calculations && calculations.length > 0 && (
              <Button onClick={handleBulkGeneration} className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Generate All PDFs
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Individual Payslips</CardTitle>
          <CardDescription>Generate and download individual payslips</CardDescription>
        </CardHeader>
        <CardContent>
          {calculations && calculations.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Gross Salary</TableHead>
                  <TableHead>Net Salary</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculations.map((calc) => (
                  <TableRow key={calc.id}>
                    <TableCell className="font-medium">
                      {calc.employees?.name || 'Unknown Employee'}
                    </TableCell>
                    <TableCell>CHF {calc.gross_salary.toFixed(2)}</TableCell>
                    <TableCell className="font-semibold">CHF {calc.net_salary.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={calc.calculation_status === 'approved' ? 'default' : 'outline'}>
                        {calc.calculation_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handlePreview(calc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Payslip Preview</DialogTitle>
                              <DialogDescription>
                                Preview for {calc.employees?.name}
                              </DialogDescription>
                            </DialogHeader>
                            {previewData && (
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-semibold">Employee</h4>
                                    <p className="text-sm">{previewData.employee.name}</p>
                                    <p className="text-sm text-muted-foreground">
                                      AHV: {previewData.employee.ahv_number}
                                    </p>
                                  </div>
                                  <div>
                                    <h4 className="font-semibold">Period</h4>
                                    <p className="text-sm">
                                      {format(new Date(previewData.period.start_date), 'MMMM yyyy')}
                                    </p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-center">
                                  <div>
                                    <p className="text-sm text-muted-foreground">Gross</p>
                                    <p className="font-semibold">CHF {previewData.payroll.gross_salary.toFixed(2)}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">Deductions</p>
                                    <p className="font-semibold text-red-600">CHF {previewData.payroll.total_deductions.toFixed(2)}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-muted-foreground">Net</p>
                                    <p className="font-semibold text-green-600">CHF {previewData.payroll.net_salary.toFixed(2)}</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGeneratePDF(calc)}
                          disabled={generatingPDF === calc.id}
                        >
                          {generatingPDF === calc.id ? (
                            'Generating...'
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-1" />
                              PDF
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No calculations found for this period
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}